import Vue from 'vue';
import axios from 'axios';
import Cookie from 'js-cookie';
import contentSort from '@/app/utils/contentSortingUtil';
import { debug } from '@/app/utils/debug';
import polling from '@/app/utils/polling';

import {
  SET_NAV_MENU_ITEMS,
  SET_ACTIVE_NAV_MENU_ITEM_ID,
  SET_STORAGE_SPACE,
  SET_SETUP_PASSWORD_STATUS,
  SET_ARCHIVE_DELETION_STATUS,
  SET_CLEANUP_STATUS,
  SET_CONTENT_ITEMS,
  SET_FETCH_NAV_MENU_ITEMS_STATUS,
  SET_FETCH_STORAGE_SPACE_STATUS,
  SET_FETCH_CONTENT_ITEMS_STATUS,
  SET_POST_LOGIN_OPEN_ARCHIVE_ID,
  SET_POST_LOGIN_OPEN_NAV_MENU_ID,
  SET_ACTIVE_CONTENT_TYPE,
  SET_SHARED_CONTENT_ITEMS,
  SET_FETCH_SHARED_CONTENT_ITEMS_STATUS,
  SET_USER_SUBSCRIPTION,
  SET_USER_SUBSCRIPTION_TYPE,
  SET_APP_FEATURES,
  SET_FILE_VERSIONS,
  SET_FETCH_FILE_VERSIONS_STATUS,
  SET_FD_LINK,
  SET_FETCH_FD_LINK_STATUS,
  SET_SIGN_FILE_LINK,
  SET_SIGN_FILE_STATUS,
  SET_SHARED_LINK,
  SET_CREATE_SHARED_LINK_STATUS,
  SET_DELETE_SHARED_LINK_STATUS,
  SET_SHARED_LINK_SETTINGS,
  SET_FETCH_SHARED_LINK_SETTINGS_STATUS,
  SET_UPDATE_SHARED_LINK_SETTINGS_STATUS,
  SET_SEARCH_ENABLED_SATE,
  SET_CLEANUP_BOX_NAME,
  SET_SPACE_FREE_AFTER_CLEANUP,
  SET_CLEANUP_PROGRESS_PERCENT,
  SET_CLEANUP_STAGE,
  SET_SEARCH_RESULT_RECEIVED,
  SET_ACTIVE_VERSION_PATH,
  SET_ACTIVE_VOLUME_PATH,
  SET_ACTIVE_FOLDER_ITEM
} from './mutation-types';

import {
  FETCH_NAV_MENU_ITEMS,
  UPDATE_ACTIVE_NAV_MENU_ITEM_ID,
  FETCH_STORAGE_SPACE,
  SETUP_ARCHIVE_PASSWORD,
  RESET_ARCHIVE_PASSWORD_STATUS,
  DELETE_ARCHIVE,
  RESET_ARCHIVE_DELETION_STATUS,
  CLEANUP_ARCHIVE,
  RESET_CLEANUP_STATUS,
  FETCH_CONTENT_ITEMS,
  SEARCH_CONTENT_ITEMS,
  UPDATE_ACTIVE_CONTENT_TYPE,
  FETCH_SHARED_CONTENT_ITEMS,
  FETCH_USER_SUBSCRIPTION,
  FETCH_FILE_VERSIONS,
  FETCH_FD_LINK,
  SIGN_FILE,
  RESET_SIGN_FILE_STATUS,
  CREATE_SHARED_LINK,
  DELETE_SHARED_LINK,
  FETCH_SHARED_LINK_SETTINGS,
  UPDATE_SHARED_LINK_SETTINGS,
  FETCH_CLEANUP_STAGE,
  START_CLEANUP_PROGRESS_POLLING,
  STOP_CLEANUP_PROGRESS_POLLING,
  UPDATE_ACTIVE_VERSION_PATH,
  UPDATE_ACTIVE_VOLUME_PATH,
  SORT_CONTENT_ITEMS,
  SORT_SHARED_CONTENT_ITEMS
} from './action-types';

import {
  PENDING_STATUS,
  SUCCESS_STATUS,
  ERROR_STATUS,
  COMPLETED_STATUS,
  LOCKED_STATUS,
  UNDEFINED_STATUS
} from './action-status';

import {
  UNDEFINED_CLEANUP_STAGE,
  RUNNING_CLEANUP_STAGE
} from './cleanup-stage';

import {
  CONTENT_TABLE_TYPE,
  UNDEFINED_CONTENT_TYPE
} from './active-content-type';

const defaultNavMenuItemId = 'BACKUPS';
const defaultNotActiveSubscriptionNavMenuItemId = 'account';

let cleanupProgressPollingEnabled = true;
const cleanupProgressPollingDelayMs = 1600;

const defaultSortOptions = { columnName: 'displayName', sortOrder: 'ascending' };
let contentTableSortOptions = defaultSortOptions;


function urlDecodeOrEmpty(value) {
  if (typeof value !== 'undefined') {
    return decodeURIComponent(value);
  }
  return '';
}

function getPostLoginOpenArchiveData() {
  const archiveId = Cookie.get('archive-id');
  const archiveName = Cookie.get('archive-name');
  return {
    id: urlDecodeOrEmpty(archiveId),
    baseName: urlDecodeOrEmpty(archiveName)
  };
}

function getUserSubscription() {
  return axios.get('/subscription');
}

let isNotSendArchivesStatistic = true;
let isNotSendSharedLinksStatistic = true;

function sendArchivesStatistic(data) {
  if (isNotSendArchivesStatistic && data) {
    isNotSendArchivesStatistic = false;

    let backups = 0;
    let archives = 0;
    let syncs = 0;

    data.forEach((el) => {
      if (el.group) {
        if (el.group === 'BACKUPS') {
          backups++;
        } else if (el.group === 'ARCHIVES') {
          archives++;
        } else if (el.group === 'SYNC') {
          syncs++;
        }
      }
    });

    Vue.gtm.push({
      event: 'Acronis',
      eventCategory: 'WR_data',
      eventAction: 'Number_of_backups',
      eventLabel: backups > 0 ? backups : 'none'
    });

    Vue.gtm.push({
      event: 'Acronis',
      eventCategory: 'WR_data',
      eventAction: 'Number_of_archives',
      eventLabel: archives > 0 ? archives : 'none'
    });

    Vue.gtm.push({
      event: 'Acronis',
      eventCategory: 'WR_data',
      eventAction: 'Number_of_syncs',
      eventLabel: syncs > 0 ? syncs : 'none'
    });
  }
}

function sendSharedLinksStatistic(data) {
  if (isNotSendSharedLinksStatistic && data) {
    isNotSendSharedLinksStatistic = false;

    const sharedLinks = data.length;

    Vue.gtm.push({
      event: 'Acronis',
      eventCategory: 'WR_data',
      eventAction: 'Number_of_shared_links',
      eventLabel: sharedLinks > 0 ? sharedLinks : 'none'
    });

    const sharedDownloads = data.reduce((accumulator, value) => accumulator + value.hitCount, 0);

    Vue.gtm.push({
      event: 'Acronis',
      eventCategory: 'WR_data',
      eventAction: 'Number_of_downloads',
      eventLabel: sharedDownloads > 0 ? sharedDownloads : 'none'
    });
  }
}

export default {
  async [FETCH_NAV_MENU_ITEMS]({ commit }) {
    commit(SET_FETCH_NAV_MENU_ITEMS_STATUS, PENDING_STATUS);
    commit(SET_NAV_MENU_ITEMS, []);
    await axios.get('/boxes')
      .then(function (response) {
        commit(SET_NAV_MENU_ITEMS, response.data);
        sendArchivesStatistic(response.data);
      }).finally(function () {
        commit(SET_FETCH_NAV_MENU_ITEMS_STATUS, UNDEFINED_STATUS);
        getUserSubscription().then(function (response) {
          // update user subscription info
          commit(SET_USER_SUBSCRIPTION, response.data);
          commit(SET_APP_FEATURES, response.data.appFeatures);
          commit(SET_USER_SUBSCRIPTION_TYPE, response.data.subscriptionType);
          if (response.data.subscriptionType === 'ACTIVE') {
            // open archive
            const archiveId = getPostLoginOpenArchiveData();
            if (archiveId.id !== '') {
              commit(SET_POST_LOGIN_OPEN_ARCHIVE_ID, archiveId);
            } else {
              const savedMenuId = window.localStorage.getItem('activeNavMenuItem');
              if (savedMenuId !== undefined && savedMenuId !== null && savedMenuId !== '') {
                commit(SET_POST_LOGIN_OPEN_NAV_MENU_ID, { id: savedMenuId });
              } else {
                commit(SET_POST_LOGIN_OPEN_NAV_MENU_ID, { id: defaultNavMenuItemId });
              }
            }
          } else {
            commit(
              SET_POST_LOGIN_OPEN_NAV_MENU_ID,
              { id: defaultNotActiveSubscriptionNavMenuItemId }
            );
          }
        });
      });
  },
  [UPDATE_ACTIVE_NAV_MENU_ITEM_ID]({ commit, dispatch }, activeNavMenuItemId) {
    if (activeNavMenuItemId !== 'resources') {
      window.localStorage.setItem('activeNavMenuItem', activeNavMenuItemId);
    }
    commit(SET_ACTIVE_NAV_MENU_ITEM_ID, activeNavMenuItemId);
    commit(SET_SEARCH_ENABLED_SATE, false);
  },
  async [FETCH_STORAGE_SPACE]({ commit }) {
    commit(SET_FETCH_STORAGE_SPACE_STATUS, PENDING_STATUS);
    await axios.get('/space')
      .then(function (response) {
        commit(SET_STORAGE_SPACE, response.data);
      }).finally(function () {
        commit(SET_FETCH_STORAGE_SPACE_STATUS, UNDEFINED_STATUS);
      });
  },
  async [SETUP_ARCHIVE_PASSWORD]({ commit, dispatch }, setupPasswordForm) {
    commit(SET_SETUP_PASSWORD_STATUS, PENDING_STATUS);
    if (debug) {
      setTimeout(function () {
        if (setupPasswordForm.password === '123') {
          commit(SET_SETUP_PASSWORD_STATUS, SUCCESS_STATUS);
        } else {
          commit(SET_SETUP_PASSWORD_STATUS, ERROR_STATUS);
        }
      }, 1200);
    } else {
      await axios.put('/password', setupPasswordForm)
        .then(function () {
          commit(SET_SETUP_PASSWORD_STATUS, SUCCESS_STATUS);
        }).catch(function (error) {
          if (error.response) {
            if (error.response.status === 403) {
              commit(SET_SETUP_PASSWORD_STATUS, ERROR_STATUS);
            } else if (error.response.status !== 200) {
              commit(SET_SETUP_PASSWORD_STATUS, UNDEFINED_STATUS);
            }
          }
        });
    }
  },
  [RESET_ARCHIVE_PASSWORD_STATUS]({ commit }) {
    commit(SET_SETUP_PASSWORD_STATUS, UNDEFINED_STATUS);
  },
  async [DELETE_ARCHIVE]({ commit, dispatch }, archiveId) {
    commit(SET_ARCHIVE_DELETION_STATUS, PENDING_STATUS);
    await axios.delete('/boxes', {
      params: {
        id: archiveId
      }
    }).then(function () {
      commit(SET_ARCHIVE_DELETION_STATUS, COMPLETED_STATUS);
    }).catch(function (error) {
      if (error.response) {
        if (error.response.status === 423) {
          commit(SET_ARCHIVE_DELETION_STATUS, LOCKED_STATUS);
        } else {
          commit(SET_ARCHIVE_DELETION_STATUS, COMPLETED_STATUS);
        }
      }
    });
  },
  [RESET_ARCHIVE_DELETION_STATUS]({ commit }) {
    commit(SET_ARCHIVE_DELETION_STATUS, UNDEFINED_STATUS);
  },
  [RESET_CLEANUP_STATUS]({ commit }) {
    commit(SET_CLEANUP_STATUS, UNDEFINED_STATUS);
  },
  [UPDATE_ACTIVE_VERSION_PATH]({ commit, dispatch }, activeVersionPath) {
    commit(SET_ACTIVE_VERSION_PATH, activeVersionPath);
  },
  [UPDATE_ACTIVE_VOLUME_PATH]({ commit, dispatch }, activeVolumePath) {
    commit(SET_ACTIVE_VOLUME_PATH, activeVolumePath);
  },
  async [FETCH_CONTENT_ITEMS]({ commit, dispatch }, selectedItem) {
    commit(SET_SEARCH_RESULT_RECEIVED, false);
    commit(SET_FETCH_CONTENT_ITEMS_STATUS, PENDING_STATUS);
    commit(SET_CONTENT_ITEMS, []);

    if (selectedItem.path === '') {
      commit(SET_ACTIVE_VERSION_PATH, '');
      commit(SET_ACTIVE_VOLUME_PATH, '');
      contentTableSortOptions = defaultSortOptions;
    }

    await axios.get('/content', {
      params: {
        archiveId: selectedItem.archiveId,
        path: selectedItem.path
      }
    }).then(function (response) {
      commit(SET_ACTIVE_FOLDER_ITEM, {
        path: selectedItem.path,
        displayName: selectedItem.path === '' ? '' : selectedItem.displayName,
        isSearchFolder: false
      });

      let content = [];
      if (debug) {
        content = response.data[0] !== undefined ? response.data[0].data : [];
      } else {
        content = response.data.data;
      }

      dispatch(SORT_CONTENT_ITEMS, {
        items: content,
        columnName: contentTableSortOptions.columnName,
        sortOrder: contentTableSortOptions.sortOrder
      });

      const isSearchable = content.length > 0 ?
        (content[0].itemGroup !== 'version' && content[0].itemGroup !== 'volume') : false;

      commit(SET_SEARCH_ENABLED_SATE, isSearchable);
    }).finally(function () {
      commit(SET_FETCH_CONTENT_ITEMS_STATUS, UNDEFINED_STATUS);
    });
  },
  async [SEARCH_CONTENT_ITEMS]({ commit, dispatch }, selectedItem) {
    commit(SET_FETCH_CONTENT_ITEMS_STATUS, PENDING_STATUS);
    commit(SET_CONTENT_ITEMS, []);

    // hack to show path column in the content table in correct order
    commit(SET_ACTIVE_CONTENT_TYPE, UNDEFINED_CONTENT_TYPE);
    commit(SET_SEARCH_RESULT_RECEIVED, true);
    await new Promise(resolve => setTimeout(resolve, 200));
    commit(SET_ACTIVE_CONTENT_TYPE, CONTENT_TABLE_TYPE);

    await axios.get('/search', {
      params: {
        archiveId: selectedItem.archiveId,
        volumePath: selectedItem.volumePath,
        path: selectedItem.path,
        searchText: selectedItem.searchText
      }
    }).then(function (response) {
      commit(SET_ACTIVE_FOLDER_ITEM, {
        path: selectedItem.path,
        displayName: selectedItem.searchText,
        isSearchFolder: true
      });

      const content = debug ? response.data : response.data.data;

      dispatch(SORT_CONTENT_ITEMS, {
        items: content,
        columnName: contentTableSortOptions.columnName,
        sortOrder: contentTableSortOptions.sortOrder
      });
    }).finally(function () {
      commit(SET_FETCH_CONTENT_ITEMS_STATUS, UNDEFINED_STATUS);
    });
  },
  [UPDATE_ACTIVE_CONTENT_TYPE]({ commit }, activeContentType) {
    commit(SET_ACTIVE_CONTENT_TYPE, activeContentType);
  },
  async [FETCH_SHARED_CONTENT_ITEMS]({ commit, dispatch }) {
    commit(SET_FETCH_SHARED_CONTENT_ITEMS_STATUS, PENDING_STATUS);
    commit(SET_SHARED_CONTENT_ITEMS, []);
    await axios.get('/shared')
      .then(function (response) {
        dispatch(SORT_SHARED_CONTENT_ITEMS, {
          items: response.data,
          columnName: 'displayName',
          sortOrder: 'ascending'
        });
        sendSharedLinksStatistic(response.data);
      }).finally(function () {
        commit(SET_FETCH_SHARED_CONTENT_ITEMS_STATUS, UNDEFINED_STATUS);
      });
  },
  async [FETCH_USER_SUBSCRIPTION]({ commit }) {
    await getUserSubscription().then(function (response) {
      commit(SET_USER_SUBSCRIPTION, response.data);
      commit(SET_APP_FEATURES, response.data.appFeatures);
      commit(SET_USER_SUBSCRIPTION_TYPE, response.data.subscriptionType);
    });
  },
  async [FETCH_FILE_VERSIONS]({ commit, dispatch }, selectedItem) {
    commit(SET_FETCH_FILE_VERSIONS_STATUS, PENDING_STATUS);
    commit(SET_FILE_VERSIONS, []);
    await axios.get('/versions', {
      params: {
        archiveId: selectedItem.archiveId,
        path: selectedItem.path
      }
    }).then(function (response) {
      const sortedData = response.data.sort((a, b) => b.id - a.id);
      commit(SET_FILE_VERSIONS, sortedData);
    }).finally(function () {
      commit(SET_FETCH_FILE_VERSIONS_STATUS, UNDEFINED_STATUS);
    });
  },
  async [FETCH_FD_LINK]({ commit, dispatch }) {
    commit(SET_FETCH_FD_LINK_STATUS, PENDING_STATUS);
    await axios.get('/fdLink')
      .then(function (response) {
        commit(SET_FD_LINK, response.data.link);
      }).finally(function () {
        commit(SET_FETCH_FD_LINK_STATUS, COMPLETED_STATUS);
      });
  },
  async [SIGN_FILE]({ commit, dispatch }, selectedItem) {
    commit(SET_SIGN_FILE_STATUS, PENDING_STATUS);
    await axios.get('/asign', {
      params: {
        archiveId: selectedItem.archiveId,
        path: selectedItem.path,
        ver: selectedItem.fileVersion
      }
    }).then(function (response) {
      commit(SET_SIGN_FILE_LINK, response.data.link);
      commit(SET_SIGN_FILE_STATUS, COMPLETED_STATUS);
    });
  },
  [RESET_SIGN_FILE_STATUS]({ commit }) {
    commit(SET_SIGN_FILE_STATUS, UNDEFINED_STATUS);
  },
  async [CREATE_SHARED_LINK]({ commit, dispatch }, selectedItem) {
    commit(SET_CREATE_SHARED_LINK_STATUS, PENDING_STATUS);
    await axios.get('/share', {
      params: {
        archiveId: selectedItem.archiveId,
        path: selectedItem.path,
        ver: selectedItem.fileVersion
      }
    }).then(function (response) {
      commit(SET_SHARED_LINK, response.data);
    }).finally(function () {
      commit(SET_CREATE_SHARED_LINK_STATUS, COMPLETED_STATUS);
    });
  },
  async [DELETE_SHARED_LINK]({ commit, dispatch }, sharedLink) {
    commit(SET_DELETE_SHARED_LINK_STATUS, PENDING_STATUS);
    await axios.delete('/share', {
      params: {
        linkId: sharedLink.linkId
      }
    }).finally(function () {
      commit(SET_DELETE_SHARED_LINK_STATUS, COMPLETED_STATUS);
    });
  },
  async [FETCH_SHARED_LINK_SETTINGS]({ commit, dispatch }, sharedLink) {
    commit(SET_FETCH_SHARED_LINK_SETTINGS_STATUS, PENDING_STATUS);
    await axios.get('/shareSettings', {
      params: {
        linkId: sharedLink.linkId
      }
    }).then(function (response) {
      const content = debug ? response.data[0] : response.data;
      commit(SET_SHARED_LINK_SETTINGS, content);
    }).finally(function () {
      commit(SET_FETCH_SHARED_LINK_SETTINGS_STATUS, COMPLETED_STATUS);
    });
  },
  async [UPDATE_SHARED_LINK_SETTINGS]({ commit, dispatch }, setupSharedLinkSettingsForm) {
    commit(SET_UPDATE_SHARED_LINK_SETTINGS_STATUS, PENDING_STATUS);
    await axios.put('/shareSettings', setupSharedLinkSettingsForm)
      .finally(function () {
        commit(SET_UPDATE_SHARED_LINK_SETTINGS_STATUS, COMPLETED_STATUS);
      });
  },
  async [CLEANUP_ARCHIVE]({ commit, dispatch }, cleanupOptions) {
    commit(SET_CLEANUP_STATUS, PENDING_STATUS);
    commit(SET_SPACE_FREE_AFTER_CLEANUP, 0);
    await axios.put('/cleanup', cleanupOptions)
      .then(function (response) {
        commit(SET_CLEANUP_STATUS, SUCCESS_STATUS);
        commit(SET_SPACE_FREE_AFTER_CLEANUP, debug ? 123 : Number(response.headers['free-space']));
      }).catch(function (error) {
        if (error.response) {
          if (error.response.status === 409) {
            commit(SET_CLEANUP_STATUS, UNDEFINED_STATUS);
            commit(SET_CLEANUP_STAGE, RUNNING_CLEANUP_STAGE);
          } else if (error.response.status === 423) {
            commit(SET_CLEANUP_STATUS, LOCKED_STATUS);
          } else {
            commit(SET_CLEANUP_STATUS, ERROR_STATUS);
          }
        }
      });
  },
  [START_CLEANUP_PROGRESS_POLLING]({ commit, dispatch }) {
    cleanupProgressPollingEnabled = true;
    commit(SET_CLEANUP_BOX_NAME, '');
    commit(SET_CLEANUP_PROGRESS_PERCENT, 0);
    polling(
      async (ctx) => {
        await axios.get('/cleanupProgress')
          .then(function (response) {
            const total = response.data.total;
            if (total > 0) {
              const processed = response.data.processed;
              let result = Math.floor((processed / total) * 100);
              result = Math.max(result, 0);
              result = Math.min(result, 100);
              commit(SET_CLEANUP_PROGRESS_PERCENT, result);
              commit(SET_CLEANUP_BOX_NAME, response.data.displayName);
            }
          }).catch(function (error) {
            cleanupProgressPollingEnabled = false;
          });

        return cleanupProgressPollingEnabled;
      },
      {
        commit: commit,
        dispatch: dispatch
      },
      cleanupProgressPollingDelayMs
    );
  },
  [STOP_CLEANUP_PROGRESS_POLLING]({ commit, dispatch }) {
    cleanupProgressPollingEnabled = false;
  },
  async [FETCH_CLEANUP_STAGE]({ commit, dispatch }, selectedItem) {
    commit(SET_CLEANUP_STAGE, UNDEFINED_CLEANUP_STAGE);
    await axios.get('/cleanupStage', {
      params: {
        archiveId: selectedItem.archiveId
      }
    }).then(function (response) {
      commit(SET_CLEANUP_STAGE, response.data.stage);
    });
  },
  [SORT_CONTENT_ITEMS]({ commit, dispatch }, data) {
    contentTableSortOptions = {
      columnName: data.columnName,
      sortOrder: data.sortOrder
    };
    const sortedItems = contentSort(data.items, data.columnName, data.sortOrder);
    commit(SET_CONTENT_ITEMS, sortedItems);
  },
  [SORT_SHARED_CONTENT_ITEMS]({ commit, dispatch }, data) {
    const sortedItems = contentSort(data.items, data.columnName, data.sortOrder);
    commit(SET_SHARED_CONTENT_ITEMS, sortedItems);
  }
};
